import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import type { PublicQuote } from "@forgepoint/shared";
import type { Editor, Range } from "@tiptap/core";
import { EditorState, Selection, TextSelection } from "@tiptap/pm/state";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { EditorContent, useEditor } from "@tiptap/react";
import { FieldSlashMenu } from "./FieldSlashMenu";
import type { FieldSuggestionState } from "./fieldSuggestion";
import {
  measureHeldSelectionRects,
  NO_HELD_SELECTION_RECTS,
  sameHeldSelectionRects,
  type HeldSelectionRect,
} from "./heldSelectionRects";
import { surfaceClipPath, type CapturedSelection } from "./overlayGeometry";
import { PROSE_INDENT_CLASS } from "./proseIndent";
import { clipAncestorBounds } from "./renderedDom";
import { resolveQuoteField } from "./quoteFields";
import { buildTextEditExtensions } from "./textEditExtensions";
import {
  CARET_MARKER,
  caretMarkdownOffset,
  type SessionCaret,
  type TextblockPoint,
} from "./textEditCaret";
import { holdsTextSession } from "./textSessionFocus";
import {
  FIELD_CHIP_NODE_NAME,
  fieldChipLabel,
  parseEditorDocJson,
  type EditorDoc,
} from "./textEditModel";

/** The longest "/query" filter kept open before the menu auto-dismisses. */
const FIELD_MENU_MAX_QUERY_LENGTH = 24;

/** Vertical offset between the "/" anchor and the field menu. */
const FIELD_MENU_OFFSET_PX = 4;

/**
 * The surface inherits the rendered block's appearance. Typography lives on
 * `.ProseMirror` itself so direct-child list rules match the rendered prose.
 * Tight lists hide TipTap's extra paragraph margins; loose lists keep them.
 * Trailing spaces hang and ligatures remain enabled, matching the renderer.
 */
const EDIT_SURFACE_CLASS = `max-w-none [font-synthesis:style] [&_*]:text-current [&_li::marker]:text-current [&_ul_ul]:list-[circle] [&_.ProseMirror>*:first-child]:mt-0 [&_.ProseMirror>*:last-child]:mb-0 [&_[data-tight=true]>li>p]:my-0 [&_.ProseMirror_li:first-child:not(:is(.ProseMirror_:not(:first-child)_*))]:mt-0 [&_.ProseMirror_li:last-child:not(:is(.ProseMirror_:not(:last-child)_*))]:mb-0 [&_.ProseMirror]:whitespace-pre-wrap [&_.ProseMirror]:[font-variant-ligatures:normal] [&_.ProseMirror]:[font-feature-settings:normal] ${PROSE_INDENT_CLASS}`;

/** Ghost-hint styling for the empty-block placeholder decoration. */
const PLACEHOLDER_CLASS =
  "[&_.is-empty]:before:pointer-events-none [&_.is-empty]:before:float-left [&_.is-empty]:before:h-0 [&_.is-empty]:before:text-gray-400 [&_.is-empty]:before:content-[attr(data-placeholder)]";

/**
 * Computed box styles copied from the block root onto the edit surface, whose
 * geometry the surface is pinned to.
 */
const BOX_PROPERTIES = [
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
  "background-color",
];

/**
 * Computed text styles copied onto the edit surface from the element that
 * actually renders the block's text (see textReference). Line height is
 * copied separately as a unitless ratio: the computed value is a resolved
 * pixel length, which would cap every session line at the block's base size
 * instead of letting a run's own font size grow its line box the way the
 * rendered block does.
 */
const TEXT_PROPERTIES = [
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "letter-spacing",
  "color",
  "text-align",
  "text-decoration",
  "text-transform",
  "text-indent",
];

/** Container-relative geometry the edit surface is pinned to. */
interface AnchorRect {
  top: number;
  left: number;
  width: number;
}

/** Open state of the field slash menu, positioned relative to the overlay. */
interface FieldMenuState {
  /** The filter text typed after the "/" so far. */
  query: string;
  /** The doc range covering the "/" and the query, replaced on insert. */
  range: Range;
  /** Overlay-relative top of the menu. */
  top: number;
  /** Overlay-relative left of the menu. */
  left: number;
}

/** Imperative controls the session overlay hands to the editor hook. */
export interface InlineTextEditControls {
  /** The live TipTap instance, so the format toolbar can drive the session. */
  editor: Editor;
  /** The committed editor doc, or null when the session made no changes. */
  getDocIfChanged: () => EditorDoc | null;
  /** The editor's current doc, or null once the editor is destroyed. */
  getDoc: () => EditorDoc | null;
  /** The caret as a markdown offset and surface position, or null without one. */
  readCaret: () => SessionCaret | null;
  /**
   * Whether the session can be handed to another fragment right now: not
   * mid-composition, not held by a toolbar control, no field menu open.
   */
  canHandOver: () => boolean;
  /** Whether a re-seed now would trim a just-typed trailing space. */
  caretAfterTrailingSpace: () => boolean;
}

interface InlineTextEditOverlayProps {
  /** Ref to the scrollable preview container the overlay is positioned in. */
  containerRef: RefObject<HTMLDivElement>;
  /** The rendered block element the session was opened on. */
  element: HTMLElement;
  /** The editor doc parsed from the block's markdown body. */
  initialDoc: EditorDoc;
  /** Whether the session root can hold block structure (lists, paragraphs). */
  blocksEnabled: boolean;
  /** The native selection the opening gesture left on the block, or null. */
  initialSelection: CapturedSelection | null;
  /** The caret to restore when the session is re-seeded, or null. */
  initialCaret: TextblockPoint | null;
  /** The public quote resolving field chips inserted during the session. */
  quote: PublicQuote;
  /** Registers the session controls with the editor hook (null on unmount). */
  registerControls: (controls: InlineTextEditControls | null) => void;
  /** Commits the session (blur, Enter outside a list). */
  onCommit: () => void;
  /** Cancels the session, discarding changes (Escape). */
  onCancel: () => void;
  /** Reports that the editor's content changed, so the draft re-renders. */
  onDraftChange: () => void;
  /** Reports that the anchored block element left the document. */
  onElementDetached: () => void;
  /**
   * Backspace in an emptied editor: steps the session back to the previous
   * page's fragment. Returns false when there is nothing before it.
   */
  onBackspaceWhenEmpty: () => boolean;
}

/**
 * In-place TipTap editing surface for one inline text-edit session: pinned
 * over the rendered block (which is hidden for the duration, with its height
 * synced to the surface so the document reflows live as the session grows),
 * visually matched through copied computed styles, with the field slash menu
 * driven by the "/" suggestion trigger and an empty block showing the ghost
 * hint at the caret. Formatting is driven from the editor's top toolbar, which
 * reaches this session through the controls it registers. Enter starts a new
 * paragraph, so a session can grow into several blocks; it commits as ONE
 * document edit on blur or Cmd/Ctrl+Enter, and cancels on Escape.
 *
 * While a toolbar control that takes real focus (the font-size box, the
 * colour popover) holds the session open, the browser stops drawing the
 * native selection, so the overlay measures it (heldSelectionRects.ts) and
 * paints its own rectangles in the `::selection` blue: above the surface's
 * copied background, below the glyphs, like the native paint order.
 *
 * Every content change is reported so the hook can render the unsaved draft
 * into the preview and repaginate it. When the draft moves the caret's text to
 * another page's fragment, the hook hands the SAME session over: `element`
 * re-anchors the surface to the new fragment and a new `initialDoc` re-seeds
 * the editor in place - focus stays, the session's undo stack starts over -
 * with the caret restored at `initialCaret`.
 * @param containerRef - The scrollable preview container
 * @param element - The rendered block element the session is anchored to
 * @param initialDoc - The editor doc seeding (or re-seeding) the session
 * @param blocksEnabled - Whether the session root can hold block structure
 * @param initialSelection - The opening gesture's native selection, or null
 * @param initialCaret - The caret to restore on a re-seed, or null
 * @param quote - The public quote resolving inserted field chips
 * @param registerControls - Registers the session controls with the hook
 * @param onCommit - Commits the session
 * @param onCancel - Cancels the session
 * @param onDraftChange - Reports an editor content change
 * @param onElementDetached - Reports the anchored element leaving the document
 * @param onBackspaceWhenEmpty - Steps an emptied session back a fragment
 * @returns The edit surface, or null until the anchor resolves
 */
export function InlineTextEditOverlay({
  containerRef,
  element,
  initialDoc,
  blocksEnabled,
  initialSelection,
  initialCaret,
  quote,
  registerControls,
  onCommit,
  onCancel,
  onDraftChange,
  onElementDetached,
  onBackspaceWhenEmpty,
}: InlineTextEditOverlayProps) {
  const [rect, setRect] = useState<AnchorRect | null>(() =>
    computeAnchorRect(containerRef.current, element),
  );
  const [fieldMenu, setFieldMenu] = useState<FieldMenuState | null>(null);
  const [heldRects, setHeldRects] = useState<HeldSelectionRect[]>(
    NO_HELD_SELECTION_RECTS,
  );
  const [clipPath, setClipPath] = useState<string | undefined>(undefined);

  const overlayRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const heldRef = useRef(false);
  const quoteRef = useRef(quote);
  quoteRef.current = quote;
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  const onElementDetachedRef = useRef(onElementDetached);
  onElementDetachedRef.current = onElementDetached;
  const onBackspaceWhenEmptyRef = useRef(onBackspaceWhenEmpty);
  onBackspaceWhenEmptyRef.current = onBackspaceWhenEmpty;
  const fieldMenuOpenRef = useRef(false);
  fieldMenuOpenRef.current = fieldMenu !== null;
  const seededDocRef = useRef(initialDoc);
  const initialJsonRef = useRef<string | null>(null);
  const dismissedFromRef = useRef<number | null>(null);

  const handleFieldMenuState = useCallback(
    (state: FieldSuggestionState | null) => {
      if (!state) {
        dismissedFromRef.current = null;
        setFieldMenu(null);
        return;
      }
      if (
        dismissedFromRef.current !== null &&
        dismissedFromRef.current !== state.range.from
      ) {
        dismissedFromRef.current = null;
      }
      if (dismissedFromRef.current === state.range.from) {
        return;
      }
      const overlayRect = overlayRef.current?.getBoundingClientRect();
      if (
        state.query.length > FIELD_MENU_MAX_QUERY_LENGTH ||
        !state.clientRect ||
        !overlayRect
      ) {
        dismissedFromRef.current = state.range.from;
        setFieldMenu(null);
        return;
      }
      setFieldMenu({
        query: state.query,
        range: state.range,
        top: state.clientRect.bottom - overlayRect.top + FIELD_MENU_OFFSET_PX,
        left: state.clientRect.left - overlayRect.left,
      });
    },
    [],
  );
  const handleFieldMenuStateRef = useRef(handleFieldMenuState);
  handleFieldMenuStateRef.current = handleFieldMenuState;

  const editor = useEditor(
    {
      extensions: buildTextEditExtensions({
        blocksEnabled,
        onCommit: () => onCommitRef.current(),
        onCancel: () => onCancelRef.current(),
        onFieldMenuState: (state) => handleFieldMenuStateRef.current(state),
      }),
      content: initialDoc,
      editorProps: {
        attributes: {
          class: "prose prose-sm max-w-none outline-none",
          style: "font-size: inherit; line-height: inherit; color: inherit;",
        },
        handleKeyDown: (view, event) =>
          event.key === "Backspace" &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.altKey &&
          !view.composing &&
          view.state.doc.childCount === 1 &&
          view.state.doc.firstChild?.isTextblock === true &&
          view.state.doc.firstChild.content.size === 0 &&
          onBackspaceWhenEmptyRef.current(),
      },
      onCreate: ({ editor: created }) => {
        initialJsonRef.current = JSON.stringify(created.getJSON());
      },
      onUpdate: () => onDraftChangeRef.current(),
      onFocus: () => {
        heldRef.current = false;
        setHeldRects(NO_HELD_SELECTION_RECTS);
      },
      // The rects are measured right here, not left to the tracking loop: the
      // browser stops drawing the native selection on this very paint, and a
      // blur is a discrete event whose state updates flush before it, so
      // painting the replacement in the same frame is what keeps the
      // handoff flicker-free.
      onBlur: ({ editor: blurred, event }) => {
        if (holdsTextSession(event.relatedTarget)) {
          heldRef.current = true;
          const surface = surfaceRef.current;
          if (surface) {
            setHeldRects(measureHeldSelectionRects(blurred.view, surface));
          }
          return;
        }
        onCommitRef.current();
      },
    },
    [],
  );

  // A table-cell root keeps painting itself: hiding the whole <td> would wipe
  // its border and background - the table's ruling - for the session, so only
  // its rendered content is hidden while the surface stands in for it. Runs
  // before paint, so a hand-over never shows the new fragment under the surface.
  useLayoutEffect(() => {
    const targets =
      element.tagName === "TD" || element.tagName === "TH"
        ? Array.from(element.children).filter(
            (child): child is HTMLElement => child instanceof HTMLElement,
          )
        : [element];
    const previousVisibility = targets.map((target) => target.style.visibility);
    const previousHeight = element.style.height;
    const previousBoxSizing = element.style.boxSizing;
    targets.forEach((target) => {
      target.style.visibility = "hidden";
    });
    return () => {
      targets.forEach((target, index) => {
        target.style.visibility = previousVisibility[index] ?? "";
      });
      element.style.height = previousHeight;
      element.style.boxSizing = previousBoxSizing;
    };
  }, [element]);

  // The hidden block's height is synced to the edit surface every frame, so
  // the document below reflows live in BOTH directions - Shift+Enter adding
  // lines pushes it down, deleting lines pulls it back up - instead of the
  // surface overlapping it or leaving dead space. The surface itself sizes
  // purely to its content; the synced element never feeds back into it.
  // The same frame re-measures the held-selection highlight, so the painted
  // rectangles follow whatever the view re-renders under them, and re-copies
  // the block's appearance, so a toolbar edit that restyles the block
  // mid-session (clearing its weight or decoration prop) shows on the surface
  // immediately instead of only after the commit. A layout effect, so a
  // hand-over cancels the old element's loop in the same flush that restores
  // that element: a frame slipping in between would pin a stale height on it.
  // A detached element is reported for a hand-over and its frame skipped.
  useLayoutEffect(() => {
    let frame = 0;
    const track = () => {
      if (!element.isConnected) {
        onElementDetachedRef.current();
        frame = requestAnimationFrame(track);
        return;
      }
      const surface = surfaceRef.current;
      if (surface) {
        copyAppearance(element, surface);
        const surfaceHeight = surface.getBoundingClientRect().height;
        if (
          surfaceHeight > 0 &&
          surfaceHeight !== element.getBoundingClientRect().height
        ) {
          element.style.boxSizing = "border-box";
          element.style.height = `${surfaceHeight}px`;
        }
      }
      const held =
        heldRef.current && surface
          ? measureHeldSelectionRects(editor.view, surface)
          : NO_HELD_SELECTION_RECTS;
      setHeldRects((previous) =>
        sameHeldSelectionRects(previous, held) ? previous : held,
      );
      const nextClip = computeSurfaceClip(
        containerRef.current,
        element,
        surface,
      );
      setClipPath((previous) => (previous === nextClip ? previous : nextClip));
      const next = computeAnchorRect(containerRef.current, element);
      if (next) {
        setRect((previous) => keepAnchorRect(previous, next));
      }
      frame = requestAnimationFrame(track);
    };
    frame = requestAnimationFrame(track);
    return () => cancelAnimationFrame(frame);
  }, [containerRef, editor, element]);

  // A hand-over re-anchors the surface to a new fragment element: it moves
  // before the next paint rather than waiting a frame for the tracking loop.
  useLayoutEffect(() => {
    const next = computeAnchorRect(containerRef.current, element);
    if (next) {
      setRect((previous) => keepAnchorRect(previous, next));
    }
  }, [containerRef, element]);

  // A hand-over re-seeds the live editor in place with the new fragment's
  // draft slice: a fresh state (so the session's undo stack cannot step back
  // into the previous fragment's content) on the same view, which keeps focus.
  // The scroll waits a frame, for the surface to move to the new fragment.
  useLayoutEffect(() => {
    if (seededDocRef.current === initialDoc || editor.isDestroyed) {
      return;
    }
    seededDocRef.current = initialDoc;
    reseedEditor(editor, initialDoc, initialCaret);
    initialJsonRef.current = JSON.stringify(editor.getJSON());
    const frame = requestAnimationFrame(() => {
      if (!editor.isDestroyed) {
        editor.commands.scrollIntoView();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [editor, initialDoc, initialCaret]);

  // The first clip is applied before the browser paints the freshly mounted
  // surface: the tracking loop's first frame runs after that paint, so a
  // cross-page block would otherwise flash its full flow through the
  // inter-page gap for a frame. Keyed on `rect` because the surface only
  // mounts once the anchor rect resolves.
  useLayoutEffect(() => {
    setClipPath(
      computeSurfaceClip(containerRef.current, element, surfaceRef.current),
    );
  }, [containerRef, element, rect]);

  // Waits for the first commit that actually mounts the surface (rect may be
  // null on mount): EditorContent's own effect has attached the view by then,
  // so the captured caret points can be hit-tested against it.
  const selectionAppliedRef = useRef(false);
  useEffect(() => {
    if (rect === null || selectionAppliedRef.current) {
      return;
    }
    selectionAppliedRef.current = true;
    applyInitialSelection(editor, initialSelection);
  }, [editor, initialSelection, rect]);

  useEffect(() => {
    registerControls({
      editor,
      getDocIfChanged: () => {
        if (editor.isDestroyed) {
          return null;
        }
        const json = editor.getJSON();
        if (
          initialJsonRef.current !== null &&
          JSON.stringify(json) === initialJsonRef.current
        ) {
          return null;
        }
        return parseEditorDocJson(json);
      },
      getDoc: () =>
        editor.isDestroyed ? null : parseEditorDocJson(editor.getJSON()),
      readCaret: () => readSessionCaret(editor, surfaceRef.current),
      canHandOver: () =>
        !editor.isDestroyed &&
        !editor.view.composing &&
        !heldRef.current &&
        !fieldMenuOpenRef.current,
      caretAfterTrailingSpace: () =>
        !editor.isDestroyed && caretAfterTrailingSpace(editor),
    });
    return () => registerControls(null);
  }, [editor, registerControls]);

  const applyAppearance = useCallback(
    (surface: HTMLDivElement | null) => {
      surfaceRef.current = surface;
      if (!surface) {
        return;
      }
      copyAppearance(element, surface);
    },
    [element],
  );

  if (!rect) {
    return null;
  }

  const insertField = (source: string) => {
    const menu = fieldMenu;
    setFieldMenu(null);
    if (!menu) {
      return;
    }
    editor
      .chain()
      .focus()
      .insertContentAt(menu.range, {
        type: FIELD_CHIP_NODE_NAME,
        attrs: {
          source,
          label: fieldChipLabel(
            source,
            resolveQuoteField(quoteRef.current, source),
          ),
        },
      })
      .run();
  };

  const dismissFieldMenu = () => {
    dismissedFromRef.current = fieldMenu?.range.from ?? null;
    setFieldMenu(null);
  };

  return (
    <div
      ref={overlayRef}
      className="absolute z-40"
      style={{ top: rect.top, left: rect.left, width: rect.width }}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div
        ref={applyAppearance}
        className={`relative ${EDIT_SURFACE_CLASS} ${PLACEHOLDER_CLASS}`}
        style={{ clipPath }}
      >
        {heldRects.length > 0 && (
          <div className="pointer-events-none absolute inset-0 z-0">
            {heldRects.map((held) => (
              <div
                key={`${held.top}:${held.left}`}
                className="absolute bg-blue-500/30"
                style={{
                  top: held.top,
                  left: held.left,
                  width: held.width,
                  height: held.height,
                }}
              />
            ))}
          </div>
        )}
        <div className="relative z-[1]">
          <EditorContent editor={editor} />
        </div>
      </div>
      {fieldMenu && (
        <FieldSlashMenu
          top={fieldMenu.top}
          left={fieldMenu.left}
          query={fieldMenu.query}
          onSelect={insertField}
          onDismiss={dismissFieldMenu}
        />
      )}
    </div>
  );
}

/**
 * Restores the selection the opening gesture made on the rendered block: the
 * surface is pinned over the block with matched appearance, so the captured
 * viewport caret points hit-test straight into the mounted view - a drag
 * selection carries over as a range, a plain click as a caret at the clicked
 * spot. Without points (or when they miss) the caret falls back to the end.
 * @param editor - The session's TipTap instance
 * @param points - The captured native selection, or null
 */
function applyInitialSelection(
  editor: Editor,
  points: CapturedSelection | null,
): void {
  const view = editor.view;
  const anchor =
    points && view.dom.isConnected
      ? view.posAtCoords({ left: points.anchor.x, top: points.anchor.y })
      : null;
  const head =
    points && view.dom.isConnected
      ? view.posAtCoords({ left: points.head.x, top: points.head.y })
      : null;
  if (!anchor || !head) {
    editor.commands.focus("end");
    return;
  }
  const doc = view.state.doc;
  view.dispatch(
    view.state.tr.setSelection(
      TextSelection.between(doc.resolve(anchor.pos), doc.resolve(head.pos)),
    ),
  );
  editor.commands.focus();
}

/**
 * Replaces the editor's state with a freshly seeded doc, the caret placed at
 * the given textblock point (or the end when it cannot be placed).
 * @param editor - The session's TipTap instance
 * @param initialDoc - The editor doc to seed
 * @param caret - The caret to restore, or null
 */
function reseedEditor(
  editor: Editor,
  initialDoc: EditorDoc,
  caret: TextblockPoint | null,
): void {
  const doc = editor.schema.nodeFromJSON(initialDoc);
  const pos = caret ? textblockPosition(doc, caret) : null;
  editor.view.updateState(
    EditorState.create({
      doc,
      plugins: editor.state.plugins,
      selection:
        pos === null ? Selection.atEnd(doc) : TextSelection.create(doc, pos),
    }),
  );
}

/**
 * Resolves a textblock point to a document position.
 * @param doc - The editor document
 * @param caret - The textblock index and offset to resolve
 * @returns The position, or null when the doc has no such textblock
 */
function textblockPosition(
  doc: ProseMirrorNode,
  caret: TextblockPoint,
): number | null {
  const textblocks: { node: ProseMirrorNode; pos: number }[] = [];
  doc.descendants((node, pos) => {
    if (!node.isTextblock) {
      return true;
    }
    textblocks.push({ node, pos });
    return false;
  });
  const target = textblocks[caret.block];
  if (!target) {
    return null;
  }
  return target.pos + 1 + Math.min(caret.offset, target.node.content.size);
}

/**
 * Reads the session's caret for a hand-over: its offset into the editor's
 * serialized markdown, found by serializing a copy of the doc with the caret
 * marker typed at the caret, and its line's middle relative to the surface.
 * @param editor - The session's TipTap instance
 * @param surface - The edit surface, or null before mount
 * @returns The caret, or null when it is not in text
 */
function readSessionCaret(
  editor: Editor,
  surface: HTMLElement | null,
): SessionCaret | null {
  if (editor.isDestroyed || !surface) {
    return null;
  }
  const { state, view } = editor;
  const head = state.selection.head;
  if (!state.doc.resolve(head).parent.inlineContent) {
    return null;
  }
  const marked = parseEditorDocJson(
    state.tr.insertText(CARET_MARKER, head).doc.toJSON(),
  );
  const markdownOffset = marked ? caretMarkdownOffset(marked) : null;
  if (markdownOffset === null) {
    return null;
  }
  const coords = view.coordsAtPos(head);
  return {
    markdownOffset,
    top: (coords.top + coords.bottom) / 2 - surface.getBoundingClientRect().top,
  };
}

/**
 * Whether the caret sits at the end of its textblock right after whitespace -
 * text a re-seed drops, since parsing trims a block's trailing whitespace. A
 * trailing hard break or an empty line is not: both serialize as a held-open
 * blank line, which survives the re-seed.
 * @param editor - The session's TipTap instance
 * @returns True when a hand-over now would swallow what was just typed
 */
function caretAfterTrailingSpace(editor: Editor): boolean {
  const { $head } = editor.state.selection;
  if ($head.parentOffset !== $head.parent.content.size) {
    return false;
  }
  const last = $head.parent.lastChild;
  return last?.isText === true && /\s$/.test(last.text ?? "");
}

/**
 * Keeps the previous anchor rect when the next one describes the same spot, so
 * the tracking loop only re-renders when the block moved.
 * @param previous - The current anchor rect, or null
 * @param next - The freshly measured anchor rect
 * @returns The rect to keep in state
 */
function keepAnchorRect(
  previous: AnchorRect | null,
  next: AnchorRect,
): AnchorRect {
  return previous &&
    previous.top === next.top &&
    previous.left === next.left &&
    previous.width === next.width
    ? previous
    : next;
}

/**
 * Copies the rendered block's computed appearance onto the edit surface, so
 * the session shows exactly what the block renders - underline included.
 * @param element - The rendered block element the session was opened on
 * @param surface - The edit surface to style
 */
function copyAppearance(element: HTMLElement, surface: HTMLElement): void {
  const box = window.getComputedStyle(element);
  const text = window.getComputedStyle(textReference(element));
  const write = (property: string, value: string) => {
    if (surface.style.getPropertyValue(property) !== value) {
      surface.style.setProperty(property, value);
    }
  };
  BOX_PROPERTIES.forEach((property) =>
    write(property, box.getPropertyValue(property)),
  );
  TEXT_PROPERTIES.forEach((property) =>
    write(property, text.getPropertyValue(property)),
  );
  write("line-height", proportionalLineHeight(text));
}

/**
 * The element whose computed text styling the surface mirrors. A table-cell
 * root renders its text through a nested prose block whose classes set their
 * own font size, so the cell's own computed font is not what its text shows;
 * every other block root renders its text itself.
 * @param element - The rendered block element the session was opened on
 * @returns The element rendering the block's text
 */
function textReference(element: HTMLElement): HTMLElement {
  if (element.tagName !== "TD" && element.tagName !== "TH") {
    return element;
  }
  const child = element.firstElementChild;
  return child instanceof HTMLElement ? child : element;
}

/**
 * The block's line height as the unitless ratio the pixel value resolves from,
 * so a session run with its own font size gets a proportionally taller line.
 * @param computed - The rendered block's computed style
 * @returns The ratio, or the computed value verbatim when it has no pixel form
 */
function proportionalLineHeight(computed: CSSStyleDeclaration): string {
  const lineHeight = Number.parseFloat(
    computed.getPropertyValue("line-height"),
  );
  const fontSize = Number.parseFloat(computed.getPropertyValue("font-size"));
  if (!Number.isFinite(lineHeight) || !Number.isFinite(fontSize) || !fontSize) {
    return computed.getPropertyValue("line-height");
  }
  return `${Math.round((lineHeight / fontSize) * 1000) / 1000}`;
}

/**
 * Computes the clip-path pinning the edit surface inside the page box (or
 * slice viewport) that clips the rendered block it stands in for. The surface
 * mounts in the overlay layer outside those clipping ancestors, so a session
 * on a block whose section is sliced across pages would otherwise paint its
 * full continuous flow through the inter-page gap and over the next page's
 * slice copy; clipped, the surface shows only its own page's part while the
 * other slice copies keep painting theirs.
 * @param container - The scrollable preview container, or null before mount
 * @param element - The rendered block element the session was opened on
 * @param surface - The edit surface being clipped, or null before mount
 * @returns The inset() clip-path, or undefined when nothing clips the block
 */
function computeSurfaceClip(
  container: HTMLDivElement | null,
  element: HTMLElement,
  surface: HTMLElement | null,
): string | undefined {
  if (!container || !surface || !element.isConnected) {
    return undefined;
  }
  const bounds = clipAncestorBounds(container, element);
  if (!bounds) {
    return undefined;
  }
  const rect = surface.getBoundingClientRect();
  return surfaceClipPath(
    { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom },
    bounds,
  );
}

/**
 * Computes the container-relative geometry of the rendered block.
 * @param container - The scrollable preview container, or null before mount
 * @param element - The rendered block element
 * @returns The anchor rect, or null when the geometry can't be measured
 */
function computeAnchorRect(
  container: HTMLDivElement | null,
  element: HTMLElement,
): AnchorRect | null {
  if (!container || !element.isConnected) {
    return null;
  }
  const blockRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  return {
    top: blockRect.top - containerRect.top + container.scrollTop,
    left: blockRect.left - containerRect.left + container.scrollLeft,
    width: blockRect.width,
  };
}
